
import { ArrowUp } from "lucide-react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  
  // Calculate how far the user has scrolled as a percentage
  const calculateScrollProgress = () => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    const clientHeight = document.documentElement.clientHeight || document.body.clientHeight;
    
    const windowHeight = scrollHeight - clientHeight;
    const scrolled = Math.max(0, Math.min(100, Math.round((scrollTop / windowHeight) * 100)));
    
    setScrollProgress(scrolled);
    setIsScrolling(true);
    setIsVisible(false); // Hide button while scrolling
  };
  
  useEffect(() => {
    let scrollTimer: ReturnType<typeof setTimeout>;
    let showTimer: ReturnType<typeof setTimeout>;
    
    const handleScroll = () => {
      calculateScrollProgress();
      
      // Clear existing timers
      clearTimeout(scrollTimer);
      clearTimeout(showTimer);
      
      // Set timer to detect when scrolling stops
      scrollTimer = setTimeout(() => {
        setIsScrolling(false);
        
        // Show button after user stops scrolling for 0.5 seconds, only if scrolled more than 20%
        if (scrollProgress > 20) {
          showTimer = setTimeout(() => {
            setIsVisible(true);
          }, 500);
        }
      }, 150); // Detect scroll stop after 150ms of no scroll events
    };
    
    window.addEventListener("scroll", handleScroll);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimer);
      clearTimeout(showTimer);
    };
  }, [scrollProgress]);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  
  // Calculate the circle circumference
  const radius = 46; // Same as the circle radius in SVG
  const circumference = 2 * Math.PI * radius;
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          className="fixed right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-full bg-punto text-white flex items-center justify-center shadow-lg z-40"
          style={{ bottom: "calc(96px + var(--ph-sticky-offset, 0px))" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          onClick={scrollToTop}
          aria-label="Volver arriba"
          title="Volver arriba"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Circular progress indicator */}
            <svg className="w-full h-full absolute inset-0" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle 
                className="text-punto-dark/30 stroke-current fill-none" 
                cx="50" 
                cy="50" 
                r={radius} 
                strokeWidth="4"
              />
              
              {/* Progress circle - using stroke-dashoffset for progress */}
              <circle 
                className="text-white stroke-current fill-none" 
                cx="50" 
                cy="50" 
                r={radius} 
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference - (circumference * scrollProgress) / 100}
                transform="rotate(-90 50 50)"
                style={{ 
                  transition: "stroke-dashoffset 0.3s ease"
                }}
              />
            </svg>
            
            {/* Arrow icon */}
            <div className="relative z-10 flex items-center justify-center">
              <ArrowUp className="h-5 w-5" />
            </div>
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default ScrollToTopButton;
